import React from "react";
import { useHistory } from "react-router-dom";

import { Grid, Typography } from "@material-ui/core";

import {
  FunctionField,
  Labeled,
  Loading,
  NumberField,
  SelectField,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useShowController,
} from "react-admin";

import { BackButton } from "../../../components/common/BackButton";
import { CustomFieldsMobile } from "../../../components/common/CustomFields";
import { ForceTitle } from "../../../components/common/ForceTitle";
import { ProductLinkIcon } from "../../../components/common/ProductLinkIcon";
import { InventoryStatus, LotTypes } from "../../../utils/constants";
import { getAreaName, getZoneName } from "../../../utils/locationUtils";
import { getUnitName } from "../../../utils/loadTypeUtils";
import {
  getBackPath,
  quantityRenderer,
  trimDecimal,
} from "../../../utils/utils";
import { InventoryAside } from "./InventoryAside";
import { useUser } from "../../../authenticatorApp/UserContext";
import { showWeight } from "../../../managerApp/resources/inventory/InventoryUitls";

const InventorySetupTab = (props) => {
  const { appUser } = useUser();
  const { record } = props;
  const yardCommodities = appUser.reloadYard.activeYardCommodities.items || [];

  return (
    <Grid container direction="column" spacing={0}>
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Product Name">
            <TextField source="productName" />
          </Labeled>
          <ProductLinkIcon {...props} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Lot Number">
            <TextField source="lotNumber" />
          </Labeled>
        </Grid>
      </Grid>

      {record.status !== InventoryStatus.STORED &&
        record.status !== InventoryStatus.SHIPPED && (
          <Grid container item direction="row" spacing={1}>
            <Grid item xs={12} md={6}>
              <Labeled label="Quantity Advised">
                <NumberField source="quantityAdvised" />
              </Labeled>
              <Typography>{getUnitName(record.inventoryUOM)}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Labeled label="Quantity Received">
                <NumberField source="quantityReceived" />
              </Labeled>
            </Grid>
          </Grid>
        )}

      {(record.status === InventoryStatus.STORED ||
        record.status === InventoryStatus.SHIPPED) && (
        <Grid container item direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <Labeled label="Quantity On Hand">
              <NumberField source="quantityOnHand" />
            </Labeled>
            <Typography>{getUnitName(record.inventoryUOM)}</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            {record.quantityMismatch ? (
              <>
                <Labeled label="Quantity Physical">
                  <NumberField source="quantityPhysical" />
                </Labeled>
              </>
            ) : null}
          </Grid>
        </Grid>
      )}
      {(record.status === InventoryStatus.STORED ||
        record.status === InventoryStatus.SHIPPED) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <Labeled label="Quantity Committed">
              <NumberField source="quantityCommitted" />
            </Labeled>
          </Grid>
          <Grid item xs={12} md={6}>
            <Labeled label="Quantity Available">
              <FunctionField
                source="quantityAvailable"
                render={(record) =>
                  trimDecimal(
                    record.quantityOnHand - record.quantityCommitted,
                    3,
                  )
                }
              />
            </Labeled>
          </Grid>
        </Grid>
      )}
      {showWeight(record?.account, record) && (
        <Grid container direction="row" spacing={1}>
          <Grid item xs={12} md={6}>
            <Labeled label="Weight">
              <FunctionField
                render={(record) =>
                  quantityRenderer(
                    record.weight,
                    `${record.weightUOM}/${record.inventoryUOM}`,
                  )
                }
              />
            </Labeled>
          </Grid>
        </Grid>
      )}
      <Grid container direction="row" spacing={1}>
        <Grid item xs={12} md={6}>
          <Labeled label="Lot Type">
            <SelectField source="lotType" choices={LotTypes} />
          </Labeled>
        </Grid>
        <Grid item xs={12} md={6}>
          <Labeled label="Load Type">
            <SelectField
              source="reloadYardCommodityId"
              choices={yardCommodities}
              optionText="commodity.name"
            />
          </Labeled>
        </Grid>
        {record.customFields && (
          <CustomFieldsMobile
            recordFields={record.customFields}
            accountFields={record.account?.inventoryCustomFields}
            width={6}
          />
        )}
      </Grid>

      <Grid item container direction="row" spacing={1}>
        <Grid item xs={12}>
          <Typography variant="overline" color="textSecondary">
            Location:
          </Typography>
        </Grid>
        <Grid item md={6}>
          <Labeled label="Area">
            <FunctionField render={(record) => getAreaName(record.areaId)} />
          </Labeled>
        </Grid>
        <Grid item md={6}>
          <Labeled label="Zone">
            <FunctionField
              render={(record) => getZoneName(record.areaId, record.zoneId)}
            />
          </Labeled>
        </Grid>
      </Grid>
    </Grid>
  );
};

const ShowActions = (props) => (
  <TopToolbar>
    <BackButton {...props} />
  </TopToolbar>
);

export const InventoryShow = (props) => {
  const { record } = useShowController(props);
  const history = useHistory();
  const backPath = getBackPath(props);

  const inbound =
    props.location?.state?.inboundRecord || record?.inboundShipment;

  if (!record?.account || !inbound) return <Loading />;
  return (
    <Show
      {...props}
      title={<ForceTitle title="View Inventory" />}
      actions={<ShowActions {...props} backPath={backPath} />}
      aside={
        <InventoryAside {...props} history={history} backPath={backPath} />
      }
    >
      <TabbedShowLayout>
        <Tab label="Inventory Setup">
          <InventorySetupTab {...props} history={history} backPath={backPath} />
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
